import { useState, useEffect, useCallback } from 'react'
import { M365AuthenticationMethod, M365AuthMethodsResponse, CreateTAPResponse, AuthMethodType } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface AuthenticationSectionProps {
  userId: string
  customerId: string
  customerTenantId: string
}

type PhoneType = 'mobile' | 'alternateMobile' | 'office'

interface PhoneMethodForm {
  phoneNumber: string
  phoneType: PhoneType
}

export function AuthenticationSection({
  userId,
  customerId,
  customerTenantId,
}: AuthenticationSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [methods, setMethods] = useState<M365AuthenticationMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{ method: M365AuthenticationMethod | null; confirming: boolean }>({
    method: null,
    confirming: false,
  })
  const [deleting, setDeleting] = useState(false)

  // TAP modal state
  const [showTAPModal, setShowTAPModal] = useState(false)
  const [tapLifetime, setTapLifetime] = useState(60)
  const [tapIsUsableOnce, setTapIsUsableOnce] = useState(true)
  const [creatingTAP, setCreatingTAP] = useState(false)
  const [tapResult, setTapResult] = useState<CreateTAPResponse | null>(null)
  const [tapError, setTapError] = useState<string | null>(null)

  // Add method dropdown state
  const [showAddDropdown, setShowAddDropdown] = useState(false)

  // Phone method modal state
  const [phoneModal, setPhoneModal] = useState<{
    show: boolean
    editing: M365AuthenticationMethod | null
    form: PhoneMethodForm
    saving: boolean
    error: string | null
  }>({
    show: false,
    editing: null,
    form: { phoneNumber: '', phoneType: 'mobile' },
    saving: false,
    error: null,
  })

  // Email method modal state
  const [emailModal, setEmailModal] = useState<{
    show: boolean
    emailAddress: string
    saving: boolean
    error: string | null
  }>({
    show: false,
    emailAddress: '',
    saving: false,
    error: null,
  })

  const fetchAuthMethods = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365UserAuthMethods(token, customerId, userId) as M365AuthMethodsResponse

      setMethods(response.methods || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load authentication methods'
      if (errorMessage.includes('Authorization_RequestDenied') || errorMessage.includes('Access denied')) {
        setError('Insufficient permissions. UserAuthenticationMethod.ReadWrite.All permission and Authentication Administrator GDAP role are required.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, customerId, customerTenantId, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchAuthMethods()
  }, [fetchAuthMethods])

  const handleDelete = async (method: M365AuthenticationMethod) => {
    try {
      setDeleting(true)
      const token = await getCustomerGraphAccessToken(customerTenantId)
      await api.deleteM365UserAuthMethod(token, customerId, userId, method.methodType, method.id)

      // Optimistic UI update - immediately remove from local state
      setMethods(prev => prev.filter(m => m.id !== method.id))
      setDeleteModal({ method: null, confirming: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete authentication method')
    } finally {
      setDeleting(false)
    }
  }

  const handleCreateTAP = async () => {
    try {
      setCreatingTAP(true)
      setTapError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const result = await api.createM365UserTAP(token, customerId, userId, {
        lifetimeInMinutes: tapLifetime,
        isUsableOnce: tapIsUsableOnce,
      }) as CreateTAPResponse

      setTapResult(result)
      // Refresh to show the new TAP in the list
      await fetchAuthMethods()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create Temporary Access Pass'
      setTapError(errorMessage)
    } finally {
      setCreatingTAP(false)
    }
  }

  const closeTAPModal = () => {
    setShowTAPModal(false)
    setTapResult(null)
    setTapError(null)
    setTapLifetime(60)
    setTapIsUsableOnce(true)
  }

  // Phone method handlers
  const openAddPhoneModal = () => {
    setPhoneModal({
      show: true,
      editing: null,
      form: { phoneNumber: '', phoneType: 'mobile' },
      saving: false,
      error: null,
    })
    setShowAddDropdown(false)
  }

  const openEditPhoneModal = (method: M365AuthenticationMethod) => {
    setPhoneModal({
      show: true,
      editing: method,
      form: {
        phoneNumber: method.phoneNumber || '',
        phoneType: method.phoneType || 'mobile',
      },
      saving: false,
      error: null,
    })
  }

  const closePhoneModal = () => {
    setPhoneModal({
      show: false,
      editing: null,
      form: { phoneNumber: '', phoneType: 'mobile' },
      saving: false,
      error: null,
    })
  }

  const handleSavePhoneMethod = async () => {
    try {
      setPhoneModal(prev => ({ ...prev, saving: true, error: null }))

      const token = await getCustomerGraphAccessToken(customerTenantId)

      if (phoneModal.editing) {
        // Update existing
        await api.updateM365UserPhoneMethod(
          token,
          customerId,
          userId,
          phoneModal.editing.id,
          {
            phoneNumber: phoneModal.form.phoneNumber,
            phoneType: phoneModal.form.phoneType,
          }
        )

        // Optimistic UI update - immediately update local state
        // Graph API has eventual consistency, so re-fetching may return stale data
        setMethods(prev => prev.map(method =>
          method.id === phoneModal.editing!.id
            ? { ...method, phoneNumber: phoneModal.form.phoneNumber, phoneType: phoneModal.form.phoneType }
            : method
        ))
      } else {
        // Add new
        const result = await api.addM365UserPhoneMethod(token, customerId, userId, {
          phoneNumber: phoneModal.form.phoneNumber,
          phoneType: phoneModal.form.phoneType,
        }) as M365AuthenticationMethod

        // Optimistic UI update - add the new method to state
        if (result && result.id) {
          setMethods(prev => [...prev, result])
        } else {
          // If we don't get a proper response, fetch to get the new list
          await fetchAuthMethods()
        }
      }

      closePhoneModal()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save phone method'
      setPhoneModal(prev => ({ ...prev, saving: false, error: errorMessage }))
    }
  }

  // Email method handlers
  const openAddEmailModal = () => {
    setEmailModal({
      show: true,
      emailAddress: '',
      saving: false,
      error: null,
    })
    setShowAddDropdown(false)
  }

  const closeEmailModal = () => {
    setEmailModal({
      show: false,
      emailAddress: '',
      saving: false,
      error: null,
    })
  }

  const handleSaveEmailMethod = async () => {
    try {
      setEmailModal(prev => ({ ...prev, saving: true, error: null }))

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const result = await api.addM365UserEmailMethod(token, customerId, userId, emailModal.emailAddress) as M365AuthenticationMethod

      // Optimistic UI update - add the new method to state
      if (result && result.id) {
        setMethods(prev => [...prev, result])
      } else {
        // If we don't get a proper response, fetch to get the new list
        await fetchAuthMethods()
      }

      closeEmailModal()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save email method'
      setEmailModal(prev => ({ ...prev, saving: false, error: errorMessage }))
    }
  }

  const getMethodIcon = (methodType: AuthMethodType) => {
    switch (methodType) {
      case 'phone':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        )
      case 'email':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      case 'microsoftAuthenticator':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        )
      case 'fido2':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        )
      case 'softwareOath':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'temporaryAccessPass':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )
      case 'password':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        )
      case 'windowsHelloForBusiness':
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      default:
        return (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  const getMethodLabel = (methodType: AuthMethodType) => {
    const labels: Record<AuthMethodType, string> = {
      phone: 'Phone',
      email: 'Email',
      fido2: 'Security Key (FIDO2)',
      microsoftAuthenticator: 'Microsoft Authenticator',
      softwareOath: 'TOTP App',
      temporaryAccessPass: 'Temporary Access Pass',
      password: 'Password',
      windowsHelloForBusiness: 'Windows Hello',
    }
    return labels[methodType] || methodType
  }

  const getMethodDetails = (method: M365AuthenticationMethod) => {
    switch (method.methodType) {
      case 'phone':
        const phoneTypeLabel = method.phoneType === 'mobile' ? 'Mobile' : method.phoneType === 'alternateMobile' ? 'Alternate' : 'Office'
        return `${method.phoneNumber} (${phoneTypeLabel})`
      case 'email':
        return method.emailAddress || 'Email configured'
      case 'microsoftAuthenticator':
        return method.displayName || method.deviceTag || 'Authenticator App'
      case 'fido2':
        return method.displayName || 'Security Key'
      case 'softwareOath':
        return 'TOTP configured'
      case 'temporaryAccessPass':
        if (method.isUsable === false) {
          return `Expired or used`
        }
        if (method.startDateTime && method.lifetimeInMinutes) {
          const start = new Date(method.startDateTime)
          const expiry = new Date(start.getTime() + method.lifetimeInMinutes * 60000)
          const now = new Date()
          if (expiry > now) {
            const minsRemaining = Math.round((expiry.getTime() - now.getTime()) / 60000)
            return `Expires in ${minsRemaining} min${method.isUsableOnce ? ' (one-time use)' : ''}`
          }
        }
        return 'Temporary Access Pass'
      case 'password':
        return 'Password authentication'
      case 'windowsHelloForBusiness':
        return method.displayName || 'Windows Hello configured'
      default:
        return ''
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const canDelete = (methodType: AuthMethodType) => {
    return methodType !== 'password'
  }

  const canEdit = (methodType: AuthMethodType) => {
    return methodType === 'phone'
  }

  const getMethodColor = (methodType: AuthMethodType) => {
    const colors: Record<AuthMethodType, string> = {
      phone: 'text-blue-600 bg-blue-100',
      email: 'text-purple-600 bg-purple-100',
      fido2: 'text-yellow-600 bg-yellow-100',
      microsoftAuthenticator: 'text-green-600 bg-green-100',
      softwareOath: 'text-indigo-600 bg-indigo-100',
      temporaryAccessPass: 'text-orange-600 bg-orange-100',
      password: 'text-gray-600 bg-gray-100',
      windowsHelloForBusiness: 'text-cyan-600 bg-cyan-100',
    }
    return colors[methodType] || 'text-gray-600 bg-gray-100'
  }

  // MFA Registration Status computation
  const mfaMethodTypes: AuthMethodType[] = ['phone', 'email', 'microsoftAuthenticator', 'fido2', 'softwareOath', 'windowsHelloForBusiness']
  const configuredMfaMethods = methods.filter(m => mfaMethodTypes.includes(m.methodType))
  const hasMFA = configuredMfaMethods.length > 0
  const hasActiveTAP = methods.some(m => m.methodType === 'temporaryAccessPass' && m.isUsable)

  const getMfaMethodsSummary = () => {
    const methodCounts: Record<string, number> = {}
    configuredMfaMethods.forEach(m => {
      const label = getMethodLabel(m.methodType)
      methodCounts[label] = (methodCounts[label] || 0) + 1
    })
    return Object.entries(methodCounts)
      .map(([label, count]) => count > 1 ? `${label} (${count})` : label)
      .join(', ')
  }

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading authentication methods...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Unable to load authentication methods</h3>
                <p className="mt-1 text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        {/* MFA Registration Status Banner */}
        <div className={`mb-6 p-4 rounded-lg border ${
          hasMFA
            ? 'bg-green-50 border-green-200'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start">
            {hasMFA ? (
              <svg className="h-5 w-5 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-yellow-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            <div className="ml-3 flex-1">
              <h4 className={`text-sm font-medium ${hasMFA ? 'text-green-800' : 'text-yellow-800'}`}>
                {hasMFA ? 'MFA Enabled' : 'MFA Not Configured'}
              </h4>
              <p className={`mt-1 text-sm ${hasMFA ? 'text-green-700' : 'text-yellow-700'}`}>
                {hasMFA
                  ? `This user has ${configuredMfaMethods.length} MFA method${configuredMfaMethods.length !== 1 ? 's' : ''} registered: ${getMfaMethodsSummary()}`
                  : 'This user only has password authentication. Consider adding MFA methods for better security.'}
              </p>
              {hasActiveTAP && (
                <p className="mt-1 text-sm text-orange-600 flex items-center">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Active Temporary Access Pass available
                </p>
              )}
            </div>
            <div className="ml-3 flex-shrink-0">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                hasMFA ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {hasMFA ? `${configuredMfaMethods.length} MFA` : 'No MFA'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">Authentication Methods</h3>
            <p className="mt-1 text-sm text-gray-500">
              Manage MFA and authentication options for this user
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchAuthMethods}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>

            {/* Add Method Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Method
                <svg className="ml-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showAddDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAddDropdown(false)} />
                  <div className="absolute right-0 z-20 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      <button
                        onClick={openAddPhoneModal}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Phone Number
                      </button>
                      <button
                        onClick={openAddEmailModal}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <svg className="h-4 w-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email Address
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowTAPModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Create TAP
            </button>
          </div>
        </div>

        {methods.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No authentication methods found</p>
        ) : (
          <div className="space-y-3">
            {methods.map(method => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center">
                  <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${getMethodColor(method.methodType)}`}>
                    {getMethodIcon(method.methodType)}
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      {getMethodLabel(method.methodType)}
                    </h4>
                    <p className="text-sm text-gray-500">{getMethodDetails(method)}</p>
                    {method.createdDateTime && (
                      <p className="text-xs text-gray-400">Added: {formatDate(method.createdDateTime)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {canEdit(method.methodType) && (
                    <button
                      onClick={() => openEditPhoneModal(method)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  )}
                  {canDelete(method.methodType) && (
                    <button
                      onClick={() => setDeleteModal({ method, confirming: false })}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.method && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setDeleteModal({ method: null, confirming: false })} />
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Delete Authentication Method
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this {getMethodLabel(deleteModal.method.methodType).toLowerCase()}?
                      This may prevent the user from signing in if they don't have other MFA methods.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => handleDelete(deleteModal.method!)}
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setDeleteModal({ method: null, confirming: false })}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Method Modal */}
      {phoneModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closePhoneModal} />
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-center text-gray-900">
                  {phoneModal.editing ? 'Edit Phone Method' : 'Add Phone Method'}
                </h3>
                <p className="mt-2 text-sm text-center text-gray-500">
                  {phoneModal.editing ? 'Update the phone number for MFA verification' : 'Add a phone number for MFA verification'}
                </p>
              </div>

              {phoneModal.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{phoneModal.error}</p>
                </div>
              )}

              <div className="mt-6 space-y-4">
                <div>
                  <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone-number"
                    value={phoneModal.form.phoneNumber}
                    onChange={(e) => setPhoneModal(prev => ({
                      ...prev,
                      form: { ...prev.form, phoneNumber: e.target.value }
                    }))}
                    placeholder="+1 555-123-4567"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter in E.164 format (e.g., +1 555-123-4567)</p>
                </div>

                <div>
                  <label htmlFor="phone-type" className="block text-sm font-medium text-gray-700">
                    Phone Type
                  </label>
                  <select
                    id="phone-type"
                    value={phoneModal.form.phoneType}
                    onChange={(e) => setPhoneModal(prev => ({
                      ...prev,
                      form: { ...prev.form, phoneType: e.target.value as PhoneType }
                    }))}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="mobile">Mobile (SMS & Voice)</option>
                    <option value="alternateMobile">Alternate Mobile</option>
                    <option value="office">Office (Voice only)</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={phoneModal.saving || !phoneModal.form.phoneNumber}
                  onClick={handleSavePhoneMethod}
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {phoneModal.saving ? 'Saving...' : phoneModal.editing ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  disabled={phoneModal.saving}
                  onClick={closePhoneModal}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Method Modal */}
      {emailModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeEmailModal} />
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-purple-100 rounded-full">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-medium text-center text-gray-900">
                  Add Email Method
                </h3>
                <p className="mt-2 text-sm text-center text-gray-500">
                  Add an email address for MFA verification codes
                </p>
              </div>

              {emailModal.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{emailModal.error}</p>
                </div>
              )}

              <div className="mt-6">
                <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email-address"
                  value={emailModal.emailAddress}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, emailAddress: e.target.value }))}
                  placeholder="user@example.com"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This should be a personal email, different from the user's work email
                </p>
              </div>

              <div className="mt-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={emailModal.saving || !emailModal.emailAddress}
                  onClick={handleSaveEmailMethod}
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {emailModal.saving ? 'Adding...' : 'Add Email'}
                </button>
                <button
                  type="button"
                  disabled={emailModal.saving}
                  onClick={closeEmailModal}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create TAP Modal */}
      {showTAPModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeTAPModal} />
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              {tapResult ? (
                // Show the TAP result
                <>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">Temporary Access Pass Created</h3>
                    <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Share this code with the user:</p>
                      <p className="text-2xl font-mono font-bold text-gray-900 select-all">
                        {tapResult.temporaryAccessPass}
                      </p>
                      <button
                        onClick={() => navigator.clipboard.writeText(tapResult.temporaryAccessPass)}
                        className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                      >
                        Copy to clipboard
                      </button>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      <p>Expires in {tapResult.lifetimeInMinutes} minutes</p>
                      {tapResult.isUsableOnce && <p>Can only be used once</p>}
                    </div>
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">
                        This code will only be shown once!
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Make sure to copy it before closing this dialog.
                      </p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={closeTAPModal}
                      className="w-full px-4 py-2 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700"
                    >
                      Done
                    </button>
                  </div>
                </>
              ) : (
                // Show the creation form
                <>
                  <div>
                    <div className="flex items-center justify-center w-12 h-12 mx-auto bg-orange-100 rounded-full">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-center text-gray-900">
                      Create Temporary Access Pass
                    </h3>
                    <p className="mt-2 text-sm text-center text-gray-500">
                      Generate a temporary code for the user to sign in without MFA
                    </p>
                  </div>

                  {tapError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{tapError}</p>
                    </div>
                  )}

                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="tap-lifetime" className="block text-sm font-medium text-gray-700">
                        Lifetime
                      </label>
                      <select
                        id="tap-lifetime"
                        value={tapLifetime}
                        onChange={(e) => setTapLifetime(parseInt(e.target.value))}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={120}>2 hours</option>
                        <option value={240}>4 hours</option>
                        <option value={480}>8 hours</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="tap-usable-once"
                        type="checkbox"
                        checked={tapIsUsableOnce}
                        onChange={(e) => setTapIsUsableOnce(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="tap-usable-once" className="ml-2 block text-sm text-gray-900">
                        One-time use only
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={creatingTAP}
                      onClick={handleCreateTAP}
                      className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {creatingTAP ? 'Creating...' : 'Create TAP'}
                    </button>
                    <button
                      type="button"
                      disabled={creatingTAP}
                      onClick={closeTAPModal}
                      className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
